import {
  shouldIncludeAttachment,
  type Attachment as ApiAttachment,
} from "@/api/attachment";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SupportedLocale } from "@/i18n";
import { ACCORDION_TITLE_CLASS } from "@/lib/accordion-content";
import { computeFieldSculptureEditionSummary } from "@/lib/computeFields";
import { Attachment, PublicationStatus, SculptureEdition } from "@/lib/types";
import { ExemplaireThumbnail } from "@/pages/dashboard/shared/ExemplaireThumbnail";
import { useMemo } from "react";
import { MergedDisplayData } from "../types";
import { SculptureEditionDrawer } from "./SculptureEditionDrawer";

interface SculptureEditionsAccordionProps {
  displayData: MergedDisplayData;
  sculptureEditions: SculptureEdition[];
  isSculptureEdition: boolean;
  isSculptureMaster: boolean;
  locale: SupportedLocale;
  t: (key: string) => string;
}

interface SculptureEditionRowProps {
  edition: SculptureEdition;
  displayData: MergedDisplayData;
  locale: SupportedLocale;
  t: (key: string) => string;
}

function SculptureEditionRow({
  edition,
  displayData,
  locale,
  t,
}: SculptureEditionRowProps) {
  const summary = computeFieldSculptureEditionSummary(
    {
      sculptureEditionNumeroEnregistrement:
        edition.sculptureEditionNumeroEnregistrement,
      sculptureEditionTypeEdition: edition.sculptureEditionTypeEdition,
      sculptureEditionNumerotation:
        edition.sculptureEditionNumerotation as
          | Record<string, string>
          | null
          | undefined,
      sculptureEditionDateRealisation: edition.sculptureEditionDateRealisation,
      collector: edition.collector,
    },
    { language: locale, translation: t, lineBreak: true },
  );

  // List endpoint already attaches attachments — avoid N extra fetches on accordion open.
  const listAttachments = edition.attachments as ApiAttachment[] | undefined;

  const thumbnailAttachments = useMemo(
    () =>
      (listAttachments?.filter((a) =>
        shouldIncludeAttachment(
          a,
          (edition.sculptureEditionPublicationStatus ??
            "draft") as PublicationStatus,
        ),
      ) ?? []) as Attachment[],
    [listAttachments, edition.sculptureEditionPublicationStatus],
  );

  return (
    <div className="flex items-start gap-3 w-full">
      <ExemplaireThumbnail
        attachments={thumbnailAttachments}
        type="sculptureEdition"
        alt={edition.sculptureEditionNumeroEnregistrement ?? ""}
      />
      <SculptureEditionDrawer
        edition={edition}
        displayData={displayData}
        locale={locale}
        t={t}
        trigger={
          <div className="flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors rounded p-2 -m-2">
            <div
              dangerouslySetInnerHTML={{ __html: summary || "" }}
              className="font-normal text-sm line-clamp-4"
            />
          </div>
        }
      />
    </div>
  );
}

export function SculptureEditionsAccordion({
  displayData,
  sculptureEditions,
  isSculptureEdition,
  isSculptureMaster,
  locale,
  t,
}: SculptureEditionsAccordionProps) {
  if (
    isSculptureEdition ||
    !isSculptureMaster ||
    sculptureEditions.length === 0
  ) {
    return null;
  }

  return (
    <AccordionItem value="sculpture-editions">
      <AccordionTrigger className={ACCORDION_TITLE_CLASS}>
        {t("singleArtworkSubtitleSculptureEditions")}
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 w-full">
        {sculptureEditions.map((edition) => (
          <SculptureEditionRow
            key={edition.id}
            edition={edition}
            displayData={displayData}
            locale={locale}
            t={t}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
